/**
 * Mode "Éval papier" — utilitaires purs pour créer l'évaluation de suivi
 * depuis l'aperçu PDF (2.3).
 *
 * Le choix des compétences se fait POST-aperçu (PanneauEvalSuivi dans
 * EvalPrintPage) : c'est là que la numérotation FINALE des exercices est
 * connue (après pagination). La maille du suivi = l'exercice tel qu'imprimé ;
 * dans chaque exercice imprimé, les références pédagogiques (attendu ou
 * savoir-faire) sont dédupliquées et portent chacune son choix de compétences.
 *
 * Contrat backend (EvaluationViewSet.create + create_exercices, vérifié) :
 *   POST /evaluations/  { evaluation: {...}, exercices: [...] }
 *   exercices[i] = { comp_ids: [int...], attendu_id: int }  OU
 *                  { comp_ids: [int...], savoir_faire_id: int }
 *   ⚠️ le backend teste la PRÉSENCE de la clé ('savoir_faire_id' in info),
 *   pas sa nullité → ne jamais émettre les deux clés, ni une clé à null.
 */

// Mapping compétence → id, le même pour l'assistant « J'ai déjà mon sujet »
// (AddEvaluation.jsx, via exercicesEvaluation.js).
export const COMPETENCES_PAR_MATIERE = {
    maths: [
        { id: 2, nom: 'Chercher' },
        { id: 5, nom: 'Modéliser' },
        { id: 6, nom: 'Représenter' },
        { id: 1, nom: 'Calculer' },
        { id: 4, nom: 'Raisonner' },
        { id: 3, nom: 'Communiquer' },
    ],
    français: [
        { id: 7, nom: "Comprendre et s'exprimer à l'oral" },
        { id: 8, nom: 'Lire' },
        { id: 9, nom: 'Écrire' },
        { id: 10, nom: 'Comprendre le fonctionnement de la langue' },
        { id: 11, nom: 'Acquérir des éléments de culture littéraire et artistique' },
    ],
};

const ID_CALCULER = 1;
const ID_LIRE = 8;

/** Compétence pré-cochée par défaut quand la question n'en porte aucune. */
export const compIdParDefaut = (matiere) =>
    (matiere === 'français' ? ID_LIRE : ID_CALCULER);

// Référence pédagogique d'une question : attendu (objet FK banque OU int nu
// des générés) prioritaire... non — le wizard est exclusif : sf OU attendu.
// Une BanqueQuestion porte au plus l'un des deux ; un généré n'a que attendu.
const refDeQuestion = (q) => {
    const sfId = q?.savoir_faire?.id ?? (typeof q?.savoir_faire === 'number' ? q.savoir_faire : null);
    if (sfId) {
        return { refType: 'sf', refId: sfId, libelle: q.savoir_faire_titre || q.savoir_faire?.nom || `Savoir-faire ${sfId}` };
    }
    const attId = q?.attendu?.id ?? (typeof q?.attendu === 'number' ? q.attendu : null);
    if (attId) {
        // Libellé : le NOM de l'attendu en priorité (attendu_titre côté banque,
        // _meta.attenduTitre injecté par ExerciseurCard côté générés) ; le label
        // de catégorie d'exercice n'est qu'un dernier recours.
        return {
            refType: 'attendu',
            refId: attId,
            libelle: q.attendu_titre || q.attendu?.description || q._meta?.attenduTitre
                || q._meta?.label || `Attendu ${attId}`,
        };
    }
    return null;
};

const compIdDeQuestion = (q) => {
    const id = q?.competence?.id ?? (typeof q?.competence === 'number' ? q.competence : null);
    return id || null;
};

/** Les payloads d'un bloc issu de groupQuestions/packBlocks (groupe ou single). */
export const payloadsDuBloc = (block) =>
    (block?.items ? block.items.map((it) => it.payload) : [block?.payload]).filter(Boolean);

// Références dédupliquées AU SEIN d'un bloc imprimé (interne).
const refsDuBloc = (payloads, matiere) => {
    const parKey = new Map();
    let nbSansRef = 0;
    (payloads || []).forEach((q) => {
        const ref = refDeQuestion(q);
        if (!ref) { nbSansRef += 1; return; }
        const key = `${ref.refType}:${ref.refId}`;
        let entry = parKey.get(key);
        if (!entry) {
            entry = { key, ...ref, nbQuestions: 0, compIds: new Set() };
            parKey.set(key, entry);
        }
        entry.nbQuestions += 1;
        const compId = compIdDeQuestion(q);
        if (compId) entry.compIds.add(compId);
    });
    const refs = [...parKey.values()].map(({ compIds, ...rest }) => ({
        ...rest,
        compIdsParDefaut: compIds.size > 0 ? [...compIds] : [compIdParDefaut(matiere)],
    }));
    return { refs, nbSansRef };
};

/**
 * Maille d'AFFICHAGE du panneau : un item par exercice imprimé (ordre du
 * papier), avec ses références dédupliquées localement. Le prof coche les
 * compétences PAR EXERCICE — la fusion par attendu se fait dans syntheseRefs.
 *
 * @returns {{ exercices: [{ exNum, refs }], nbSansRef: number }}
 */
export const exercicesImprimes = (blocks, matiere = 'maths') => {
    let nbSansRef = 0;
    const exercices = [...(blocks || [])]
        .sort((a, b) => (a.exNum || 0) - (b.exNum || 0))
        .map((block) => {
            const resultat = refsDuBloc(payloadsDuBloc(block), matiere);
            nbSansRef += resultat.nbSansRef;
            return { exNum: block.exNum, refs: resultat.refs };
        })
        .filter((e) => e.refs.length > 0);
    return { exercices, nbSansRef };
};

/**
 * Fusion par référence UNIQUE des compétences cochées sur les exercices —
 * la sémantique du wizard : UNE ligne de saisie par attendu, avec l'UNION
 * des compétences cochées sur tous les exercices qui le portent.
 * Ex. : attendu 653 coché "Chercher" dans l'Exercice 1 et "Calculer" dans
 * l'Exercice 3 → une entrée { compIds: [Chercher, Calculer], exNums: [1, 3] }.
 * Une ref sans aucune compétence cochée nulle part SORT du suivi (exercice
 * bonus légitime). Cette synthèse alimente l'affichage "Ce qui sera évalué"
 * ET le payload : ce que le prof voit est exactement ce qui est envoyé.
 *
 * @param {Array} exercices - sortie de exercicesImprimes
 * @param {Object} competencesParCarte - { `${exNum}:${refKey}`: [compIds] }
 */
export const syntheseRefs = (exercices, competencesParCarte) => {
    const parKey = new Map();
    (exercices || []).forEach(({ exNum, refs }) => {
        refs.forEach((ref) => {
            const cochees = competencesParCarte[`${exNum}:${ref.key}`] ?? ref.compIdsParDefaut;
            let entry = parKey.get(ref.key);
            if (!entry) {
                entry = {
                    key: ref.key, refType: ref.refType, refId: ref.refId,
                    libelle: ref.libelle, exNums: [], compExNums: new Map(),
                };
                parKey.set(ref.key, entry);
            }
            entry.exNums.push(exNum);
            // Provenance par compétence : quels exercices l'ont cochée
            cochees.forEach((id) => {
                if (!entry.compExNums.has(id)) entry.compExNums.set(id, []);
                entry.compExNums.get(id).push(exNum);
            });
        });
    });
    return [...parKey.values()]
        .map(({ compExNums, ...rest }) => ({
            ...rest,
            // comps : [{ id, exNums }] — l'affichage montre "Calculer (Ex 1, 3)"
            comps: [...compExNums].map(([id, exs]) => ({ id, exNums: exs })),
            // compIds : la liste plate pour le payload backend
            compIds: [...compExNums.keys()],
        }))
        .filter((e) => e.compIds.length > 0);
};

/**
 * Tableau `exercices` du POST : un item backend par entrée de SYNTHÈSE
 * (référence unique + union des compétences). Pas de `titre` : le backend
 * le hardcode dans create_exercices.
 */
export const buildExercices = (synthese) => synthese.map((ref) => {
    const item = { comp_ids: ref.compIds };
    if (ref.refType === 'sf') item.savoir_faire_id = ref.refId;
    else item.attendu_id = ref.refId;
    return item;
});

/**
 * Payload complet du POST /evaluations/ — même forme que le wizard
 * (AddEvaluation), niveau/cible null → branche standard (jamais le fan-out
 * devoir commun).
 */
export const buildEvaluationPayload = ({ titre, date, idclasse, estClasse, etablissementId, exercices }) => ({
    evaluation: {
        titre,
        date,
        classe: estClasse ? parseInt(idclasse, 10) : null,
        groupe: estClasse ? null : parseInt(idclasse, 10),
        niveau: null,
        cible: null,
        etablissement_id: etablissementId,
        statut: 'en_attente',
        professeurReferents: {},
    },
    exercices,
});

/**
 * Ouvre l'aperçu PDF (flux sessionStorage → /print-eval, extrait de
 * LaunchModal.handleOpenPrint pour partage). `contexte` (idclasse, estClasse,
 * etablissementId, matiere) n'est fourni qu'en type évaluation — il active le
 * panneau "Évaluation de suivi" dans l'aperçu.
 * @returns {boolean} false si le stockage local est indisponible.
 */
// Groupes de besoin en papier : un seul document (/print-groupes) avec le
// récapitulatif, les sujets et leurs corrigés.
export const openGroupesPrint = (doc) => {
    try {
        sessionStorage.setItem('groupesPrintData', JSON.stringify(doc));
        window.open('/print-groupes', '_blank');
        return true;
    } catch {
        return false;
    }
};

export const openEvalPrintPreview = (questions, meta, contexte = null) => {
    const questionsWithPos = questions.map((q, i) => ({ ...q, __position: i + 1 }));
    const payload = { selectedQuestions: questionsWithPos, meta, contexte };
    try {
        sessionStorage.setItem('evalPrintData', JSON.stringify(payload));
        window.open('/print-eval', '_blank');
        return true;
    } catch {
        return false;
    }
};
